import type { Row } from '@tanstack/react-table';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { IImportColumn, IPreviewRow } from '@feathr/blackbox';
import type { ITagListCellTag } from '@feathr/components';
import { TanStackTable } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import PreviewCard from './PreviewCard';
import previewTableColumns from './PreviewTableColumns';

import * as styles from './DataImportPreview.css';

interface IProps {
  columnMappers: IImportColumn[];
  /** The revamped wizard's shorter copy. */
  isRevampEnabled?: boolean;
  rows: IPreviewRow[];
  tagIds: string[];
}

function DataImportPreview({
  columnMappers,
  isRevampEnabled = false,
  rows,
  tagIds,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { Tags } = useStore();
  const [selectedIndex, setSelectedIndex] = useState(0);
  // Read from the current rows, so a newer preview never leaves the card on an older row.
  const index = selectedIndex < rows.length ? selectedIndex : 0;
  const selectedRow = rows[index];

  const handleRowClick = useCallback((row: Row<IPreviewRow>): void => {
    setSelectedIndex(row.index);
  }, []);

  const getRowClassName = useCallback(
    (row: Row<IPreviewRow>): string =>
      classNames({
        [styles.errorRow]: row.original.error,
        [styles.selectedRow]: row.index === index,
      }),
    [index],
  );

  /*
   * Resolved fresh on every render (not memoized): pending tags resolve their
   * names through MobX, and a memo would keep serving the pending snapshot
   * because its dependencies never change.
   */
  const tags: ITagListCellTag[] = tagIds.map((id) => {
    const tag = Tags.get(id);
    return { id, name: tag.isPending ? '' : tag.name, isPending: tag.isPending };
  });

  return (
    <div className={styles.root}>
      <h3>{isRevampEnabled ? t('Preview') : t('Import preview')}</h3>
      <div className={styles.description}>
        {isRevampEnabled ? (
          <p>
            {t(
              'How the first rows of your file will be read. Select a row to see the person it creates.',
            )}
          </p>
        ) : (
          <Trans t={t}>
            <p>
              Select a row below to see a preview of what the imported data will look like within
              Feathr. The preview only shows the first 10 rows of the import.
            </p>
          </Trans>
        )}
      </div>

      <TanStackTable<IPreviewRow>
        columns={previewTableColumns(t, columnMappers, tags)}
        getRowClassName={getRowClassName}
        idKey={'id'}
        isPaginated={false}
        items={rows}
        noDataText={t('No preview rows to display.')}
        onRowClick={handleRowClick}
      />
      {selectedRow && <PreviewCard row={selectedRow} tagIds={tagIds} />}
    </div>
  );
}

export default observer(DataImportPreview);
